/**
 * ============================================
 * USE ANALYTICS HOOK
 * ============================================
 *
 * Fetches analytics data from the backend.
 * Auto-refreshes every 24 hours.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BACKEND_URL } from '@/config';

// ============================================
// TYPES
// ============================================

export interface GeoData {
  country_code: string;
  country: string;
  players: number;
  percentage: number;
}

export interface RetentionData {
  day: string;
  rate: number;
}

export interface CampaignProgress {
  level: string;
  completion: number;
  quitRate: number;
  restartRate: number;
}

export interface LevelStats {
  level: string;
  avgTime: number | null;
  quitRate: number;
  entries: number;
  completions: number;
  restarts: number;
}

export interface EditorUsage {
  avgLevelsBuilt: number;
  avgAvatarEdited: number;
  avgNpcEdited: number;
  levelEditorUsers?: number;
  avatarEditorUsers?: number;
  npcEditorUsers?: number;
}

export interface TimeExpenditure {
  avatarEditor: number;
  npcEditor: number;
  levelEditor: number;
  editor2d: number;
  trackEditor: number;
  chassisEditor?: number;
  coopLevelEditor?: number;
  avatarEditorSamples?: number;
  npcEditorSamples?: number;
  levelEditorSamples?: number;
  editor2dSamples?: number;
  trackEditorSamples?: number;
  chassisEditorSamples?: number;
  coopLevelEditorSamples?: number;
}

export interface OnboardingStep {
  checkpoint: string;
  players: number;
  percentage: number;
}

export interface ChurnPoint {
  category: string;
  count: number;
}

export interface CinematicStats {
  skipRate: number;
  totalSkipped: number;
  totalCompleted: number;
  totalViews: number;
  byCinematic?: Array<{
    id: string;
    skipped: number;
    completed: number;
    skipRate: number;
  }>;
}

export interface FeedbackStats {
  initiated: number;
  submitted: number;
  submissionRate: number;
}

export interface LevelEditorOnboarding {
  started: number;
  completed: number;
  completionRate: number;
}

export interface EventCounts {
  total: number;
  byType: Record<string, number>;
}

export interface AvatarEditorHourly {
  hour: string;
  fullHour: string;
  count: number;
}

export interface DailySession {
  date: string;
  fullDate: string;
  avgSession: number;
  sessionCount: number;
}

export interface TimeDistributionEntry {
  area: string;
  avgTime: number;
  sessions: number;
}

export interface GlobalTimeDistribution {
  distribution: TimeDistributionEntry[];
  totalSinglePlayerTime: number;
  totalLabzTime: number;
  totalUgcTime: number;
}

export interface SessionTimeSeriesEntry {
  period: string;
  avgSessionTime: number;
  sessionCount: number;
  playerCount: number;
}

export interface LevelTimeSeriesEntry {
  period: string;
  avgLevelTime: number;
  levelCount: number;
}

export interface TimeSeriesData {
  sessionTimeSeries: SessionTimeSeriesEntry[];
  levelTimeSeries: LevelTimeSeriesEntry[];
  timeScale: string;
}

export interface EditorGrossStats {
  levelEditorTotalSeconds: number;
  levelEditorTotalTime: string;
  npcEditorTotalSeconds: number;
  npcEditorTotalTime: string;
  avatarEditorTotalSeconds: number;
  avatarEditorTotalTime: string;
  editor2dTotalSeconds: number;
  editor2dTotalTime: string;
  trackEditorTotalSeconds: number;
  trackEditorTotalTime: string;
  chassisEditorTotalSeconds?: number;
  chassisEditorTotalTime?: string;
  coopLevelEditorTotalSeconds?: number;
  coopLevelEditorTotalTime?: string;
  levelEditorOpens: number;
  avatarEditorOpens: number;
  npcEditorOpens: number;
  editor2dOpens: number;
  trackEditorOpens: number;
  chassisEditorOpens?: number;
  coopLevelEditorOpens?: number;
  levelSaves: number;
  levelPublishes: number;
  level2dSaves: number;
  level2dPublishes: number;
  trackEditorSaves: number;
  trackEditorPublishes: number;
  coopLevelEditorSaves?: number;
  coopLevelEditorPublishes?: number;
  avatarEditorHourly: AvatarEditorHourly[];
}

export interface CharacterStats {
  characters: Array<{
    character: string;
    count: number;
    percentage: number;
  }>;
  totalSelections?: number;
}

export interface TopEditorBlocks {
  blocks: Array<{
    block: string;
    count: number;
    percentage: number;
  }>;
  totalPlacements?: number;
}

export interface ContestStats {
  totalRegistered: number;
  totalSkipped: number;
  total: number;
  skipRate: number;
}

export interface AnalyticsData {
  platform: string;
  timeRange?: string;
  totalPlayers: number;
  regions: number;
  geo: GeoData[];
  avgSessionLength: number;
  dailySessions?: DailySession[];
  timeSeriesData?: TimeSeriesData;
  retention: RetentionData[];
  campaignProgress: CampaignProgress[];
  levelStats: LevelStats[];
  editorUsage: EditorUsage;
  timeExpenditure: TimeExpenditure;
  globalTimeDistribution?: GlobalTimeDistribution;
  editorGrossStats?: EditorGrossStats;
  onboardingFunnel: OnboardingStep[];
  churnPoints: ChurnPoint[];
  cinematicStats?: CinematicStats;
  feedbackStats?: FeedbackStats;
  levelEditorOnboarding?: LevelEditorOnboarding;
  eventCounts?: EventCounts;
  characterStats?: CharacterStats;
  topEditorBlocks?: TopEditorBlocks;
  topEditor2dBlocks?: TopEditorBlocks;
  contestStats?: ContestStats;
  // Per-day editor open counts: Dict[date_iso, Dict[editor_type, count]].
  dailyEditorOpens?: Record<string, Record<string, number>>;
  dailyFriendCodeInitialized?: Record<string, Record<string, number>>;
  dailyFriendCodeRedeemed?: Record<string, Record<string, number>>;
  dailyCarTrackEditorUnlocked?: Record<string, Record<string, number>>;
  // Friend requests: per-day sent count, and per-day interaction by state
  // ({ accepted, rejected }).
  dailyFriendRequestSent?: Record<string, number>;
  dailyFriendRequestInteraction?: Record<string, Record<string, number>>;
  dailyFriendUnfriended?: Record<string, number>;
  // Per-level collectibles from level_completed.items_collected, aggregated
  // by raw level_id. Currency keys are lowercased ('coin', 'star', 'orb');
  // unknown currencies may appear if the game ships a new one, hence the
  // open-ended index signature.
  levelItemsCollected?: Record<string, Record<string, number>>;
  // Counts of main_menu_button_pressed events keyed by raw button_id.
  mainMenuButtonCounts?: Record<string, number>;
  // Multiplier-pickup counts nested by source then level_id, e.g.
  // { triple_stomp: { LEVEL_A: 12, ... }, multiplier_box: { ... } }.
  multiplierPickedCounts?: Record<string, Record<string, number>>;
  // ---- Per-day additive rollups (Phase A1 date-range filter) ----
  // Keyed by ISO date. The frontend re-aggregates these across the selected
  // range (see utils/rangeData.ts) to make the corresponding all-time cards
  // range-aware without a backend recompute. Empty until a post-A1 resync.
  globalTimeAccumByDay?: Record<string, Record<string, { sum: number; count: number }>>;
  levelTimesByDay?: Record<string, Record<string, { sum: number; count: number }>>;
  editorGrossSecondsByDay?: Record<string, Record<string, number>>;
  levelSaveCountsByDay?: Record<string, Record<string, number>>;
  levelItemsCollectedByDay?: Record<string, Record<string, Record<string, number>>>;
  mainMenuButtonCountsByDay?: Record<string, Record<string, number>>;
  multiplierPickedCountsByDay?: Record<string, Record<string, Record<string, number>>>;
  characterCountsByDay?: Record<string, Record<string, number>>;
  // Per-day per-level event counts -> range-aware (attempt-based) completion /
  // quit / avg time. levelTimesByDay (above) supplies avg time.
  levelEntryCountsByDay?: Record<string, Record<string, number>>;
  levelCompletionCountsByDay?: Record<string, Record<string, number>>;
  levelRestartCountsByDay?: Record<string, Record<string, number>>;
  lastUpdated: string;
  error?: string;
}

export interface UseAnalyticsResult {
  data: AnalyticsData | null;
  platforms: string[];
  selectedPlatform: string;
  setSelectedPlatform: (platform: string) => void;
  timeRange: string;
  setTimeRange: (range: string) => void;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  refetch: () => void;
  clearCache: () => Promise<void>;
  lastFetchTime: Date | null;
  nextRefreshTime: Date | null;
}

// ============================================
// HOOK
// ============================================

// Auto-refresh interval: 24 hours
const AUTO_REFRESH_INTERVAL = 24 * 60 * 60 * 1000;

export function useAnalytics(): UseAnalyticsResult {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [platforms, setPlatforms] = useState<string[]>(['overall']);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('overall');
  const [timeRange, setTimeRange] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);
  const [nextRefreshTime, setNextRefreshTime] = useState<Date | null>(null);
  const retryCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxRetries = 999; // Keep retrying while backend sync is in progress

  // Check if backend is still fetching
  const checkFetchStatus = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/analytics/status`);
      if (response.ok) {
        const status = await response.json();
        return status.fetch_in_progress === true;
      }
    } catch (err) {
      // Ignore errors
    }
    return false;
  }, []);

  // Fetch available platforms
  const fetchPlatforms = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/analytics/platforms`);
      if (response.ok) {
        const result = await response.json();
        setPlatforms(result.platforms || ['overall']);
      }
    } catch (err) {
      console.error('Failed to fetch platforms:', err);
    }
  }, []);

  // Fetch analytics data with retry logic
  const fetchData = useCallback(async (platform: string, range: string) => {
    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    setIsLoading(true);
    setError(null);
    retryCountRef.current = 0;
    
    const attemptFetch = async (): Promise<void> => {
      try {
        const url = `${BACKEND_URL}/api/v1/analytics?platform=${encodeURIComponent(platform)}&time_range=${encodeURIComponent(range)}`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout per request
        
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        
        let result;
        try {
          result = await response.json();
        } catch (parseErr) {
          throw new Error('Invalid response from server');
        }
        
        // Check if backend is still loading (202 status or loading flag)
        if (response.status === 202 || result.loading === true) {
          setIsFetching(true);
          setIsLoading(false);
          setError(result.message || `Loading analytics data... (attempt ${retryCountRef.current + 1})`);
          
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current++;
            retryTimeoutRef.current = setTimeout(attemptFetch, 5000);
            return;
          } else {
            setError('Timeout: Analytics data is taking too long to load. Please try again later.');
            setIsFetching(false);
          }
          return;
        }
        
        // Check for HTTP errors
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Check if we got valid data
        if (result.totalPlayers !== undefined && result.totalPlayers > 0) {
          // Success - we have data
          setData(result);
          setError(null);
          setIsFetching(false);
          setIsLoading(false);
          // Track fetch times
          const now = new Date();
          setLastFetchTime(now);
          setNextRefreshTime(new Date(now.getTime() + AUTO_REFRESH_INTERVAL));
          return;
        }
        
        // Backend returned but no data yet - could be still initializing
        if (result.error || result.totalPlayers === 0) {
          setIsFetching(true);
          setIsLoading(false);
          setError(result.error || `Waiting for data... (attempt ${retryCountRef.current + 1})`);
          
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current++;
            retryTimeoutRef.current = setTimeout(attemptFetch, 5000);
            return;
          } else {
            setData(result); // Set whatever we have
            setError(result.error || 'No analytics data available');
            setIsFetching(false);
          }
          return;
        }
        
        // Fallback - set whatever data we got
        setData(result);
        setError(null);
        setIsFetching(false);
        setIsLoading(false);
        
      } catch (err) {
        console.error('Analytics fetch error:', err);
        
        // Network error or timeout - check if backend is busy before retrying
        if (retryCountRef.current < maxRetries) {
          const backendBusy = await checkFetchStatus();
          
          retryCountRef.current++;
          setIsFetching(true);
          setIsLoading(false);
          
          const errorMsg = err instanceof Error ? err.message : 'Connection error';
          if (backendBusy) {
            setError(`Backend is loading data... (attempt ${retryCountRef.current})`);
          } else {
            setError(`${errorMsg}. Retrying... (attempt ${retryCountRef.current})`);
          }
          
          retryTimeoutRef.current = setTimeout(attemptFetch, 5000);
          return;
        }
        
        // Max retries reached
        setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
        setData(null);
        setIsFetching(false);
        setIsLoading(false);
      }
    };
    
    await attemptFetch();
  }, [checkFetchStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Clear cache
  const clearCache = useCallback(async () => {
    try {
      await fetch(`${BACKEND_URL}/api/v1/analytics/cache/clear`, {
        method: 'POST'
      });
      // Refetch after clearing
      await fetchData(selectedPlatform, timeRange);
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }
  }, [selectedPlatform, timeRange, fetchData]);

  // Refetch current data
  const refetch = useCallback(() => {
    fetchData(selectedPlatform, timeRange);
  }, [selectedPlatform, timeRange, fetchData]);

  // Fetch platforms on mount
  useEffect(() => {
    fetchPlatforms();
  }, []);

  // Fetch data on mount and when platform or time range changes
  useEffect(() => {
    fetchData(selectedPlatform, timeRange);
  }, [selectedPlatform, timeRange, fetchData]);

  // Auto-refresh every 24 hours
  useEffect(() => {
    const intervalId = setInterval(() => {
      console.log('Auto-refreshing analytics data (24h interval)...');
      fetchData(selectedPlatform, timeRange);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [selectedPlatform, timeRange, fetchData]);

  return {
    data,
    platforms,
    selectedPlatform,
    setSelectedPlatform,
    timeRange,
    setTimeRange,
    isLoading,
    isFetching,
    error,
    refetch,
    clearCache,
    lastFetchTime,
    nextRefreshTime,
  };
}
